import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { COLORS, FONTS, EASE_OUT, NEUTRAL1_RGB, NEUTRAL9_RGB } from '../home/tokens'
import { CtaButton } from '../home/CtaButton'
import { type ConsentCategories } from '@/lib/consent'
import { CookieToggle } from './CookieToggle'

type Category = {
  key: 'necessary' | 'analytics' | 'marketing'
  title: string
  body: string
  locked?: boolean
}

const CATEGORIES: Category[] = [
  {
    key: 'necessary',
    title: 'Strictly necessary',
    body: 'Required for the site to function — page navigation and remembering your privacy choices. Always on.',
    locked: true,
  },
  {
    key: 'analytics',
    title: 'Analytics',
    body: 'Google Analytics, so we can understand how the site is used and improve it. Sets measurement cookies.',
  },
  {
    key: 'marketing',
    title: 'Marketing',
    body: 'HubSpot forms on our Contact and Careers pages, used to follow up on your enquiry. Sets marketing cookies.',
  },
]

/**
 * Cookie preferences modal — per-category toggles with Save / Accept all /
 * Reject all. Opens above everything (z 130). Closes on backdrop click or Esc.
 */
export function CookiePreferences({
  open,
  initial,
  onSave,
  onAcceptAll,
  onRejectAll,
  onClose,
}: {
  open: boolean
  initial: ConsentCategories
  onSave: (categories: ConsentCategories) => void
  onAcceptAll: () => void
  onRejectAll: () => void
  onClose: () => void
}) {
  const reduce = useReducedMotion()
  const [draft, setDraft] = useState<ConsentCategories>(initial)

  // Re-seed the draft each time the modal opens so it reflects the live choice.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional derived-state reset keyed on `open`; the draft is deliberately re-synced to the committed choice each time the modal opens, not on every render.
    if (open) setDraft(initial)
  }, [open, initial])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.25, ease: EASE_OUT }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 130,
            backgroundColor: 'rgba(15, 13, 11, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
        >
          <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 16, scale: reduce ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduce ? 0 : 16, scale: reduce ? 1 : 0.98 }}
            transition={reduce ? { duration: 0 } : { duration: 0.3, ease: EASE_OUT }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 560,
              maxHeight: '85vh',
              overflowY: 'auto',
              backgroundColor: COLORS.neutral8,
              borderRadius: 20,
              border: `1px solid rgba(${NEUTRAL1_RGB},0.08)`,
              boxShadow: `0 24px 64px rgba(${NEUTRAL9_RGB},0.45)`,
              padding: 'clamp(24px, 4vw, 40px)',
              boxSizing: 'border-box',
            }}
          >
            <h2
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 200,
                fontSize: 'var(--text-2xl)',
                color: COLORS.neutral2,
                margin: 0,
                marginBottom: 10,
              }}
            >
              Cookie preferences
            </h2>
            <p
              style={{
                fontFamily: FONTS.body,
                fontSize: 'var(--text-sm)',
                lineHeight: 1.6,
                color: COLORS.neutral5,
                margin: 0,
                marginBottom: 24,
              }}
            >
              We use cookies to run the site, measure usage, and support our
              contact forms. Choose which categories to allow. See our{' '}
              <Link
                to="/privacy/"
                onClick={onClose}
                style={{ color: COLORS.green1, textDecoration: 'underline' }}
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.key}
                  style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'flex-start',
                    paddingBottom: 18,
                    borderBottom: `1px solid rgba(${NEUTRAL1_RGB},0.08)`,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        fontFamily: FONTS.body,
                        fontWeight: 600,
                        fontSize: 'var(--text-base)',
                        color: COLORS.neutral1,
                        margin: 0,
                        marginBottom: 6,
                      }}
                    >
                      {cat.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 'var(--text-sm)',
                        lineHeight: 1.55,
                        color: COLORS.neutral5,
                        margin: 0,
                      }}
                    >
                      {cat.body}
                    </p>
                  </div>
                  <CookieToggle
                    label={cat.title}
                    locked={cat.locked}
                    checked={cat.locked ? true : draft[cat.key]}
                    onChange={(next) =>
                      setDraft((d) => ({ ...d, [cat.key]: next }))
                    }
                  />
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                marginTop: 28,
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <CtaButton label="Reject all" variant="ghost" onClick={onRejectAll} />
              <CtaButton label="Accept all" variant="outline" onClick={onAcceptAll} />
              <CtaButton
                label="Save preferences"
                variant="solid"
                onClick={() => onSave(draft)}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
